import { useCallback, useEffect, useState } from 'react';
import { browserName, deviceDetect } from 'react-device-detect';
import ReactNativeBiometrics from 'react-native-biometrics';
import DeviceInfo from 'react-native-device-info';
import { UserCredentials, getGenericPassword } from 'react-native-keychain';

import i18n from '@webtrader/core/i18n';
import { authService } from '@webtrader/core/services/auth';
import { checkIsNative } from '@webtrader/core/validators/device';

import { AlertManager } from '@/components/Alert/Manager';
import {
  EBiometricsType,
  biometricsPasscodeOptions,
  getBiometricEnable,
  storeBiometricEnable
} from '@/utils/biometrics';
import { checkIsIos } from '@/utils/device';

const IS_MOBILE_WEB = !checkIsNative();

export const useBiometrics = () => {
  const [biometryType, setBiometryType] = useState<EBiometricsType>(EBiometricsType.NOT_SUPPORT);
  const [isBiometricEnable, setIsBiometricEnable] = useState(false);

  const loginWithBiometrics = useCallback(async (credentials: false | UserCredentials) => {
    const deviceDetectInfo = IS_MOBILE_WEB ? deviceDetect(window.navigator.userAgent) : undefined;

    if (credentials) {
      const { success, message } = await authService.login({
        userId: credentials.username,
        password: credentials.password,
        deviceId: IS_MOBILE_WEB ? undefined : await DeviceInfo.getUniqueId(),
        browserType: IS_MOBILE_WEB ? browserName ?? undefined : undefined,
        deviceType: IS_MOBILE_WEB ? deviceDetectInfo.os ?? undefined : undefined
      });

      if (!success) {
        AlertManager().alert({
          message
        });
      }
    } else {
      AlertManager().alert({
        message: i18n.t('Failed to retrieve credentials')
      });
    }
  }, []);

  const validatePasswordForIOS = useCallback(async () => {
    try {
      const credentials = await getGenericPassword(biometricsPasscodeOptions);

      loginWithBiometrics(credentials);
    } catch (error) {}
  }, [loginWithBiometrics]);

  const validatePasswordForAndroid = useCallback(async () => {
    if (__WEB__) {
      return;
    }

    const resultObject = await ReactNativeBiometrics.simplePrompt({
      promptMessage: i18n.t('Authentication needed')
    });

    const { success } = resultObject;

    if (success) {
      const credentials = await getGenericPassword();

      loginWithBiometrics(credentials);
    } else {
    }
  }, [loginWithBiometrics]);

  const onBiometricsLoginPress = useCallback(() => {
    if (isBiometricEnable && !IS_MOBILE_WEB) {
      checkIsIos() ? validatePasswordForIOS() : validatePasswordForAndroid();
    }
  }, [isBiometricEnable, validatePasswordForIOS, validatePasswordForAndroid]);

  useEffect(() => {
    const getBiometricType = async () => {
      if (__WEB__) {
        return;
      }

      const { biometryType } = await ReactNativeBiometrics.isSensorAvailable();

      setIsBiometricEnable(getBiometricEnable());

      switch (biometryType) {
        case ReactNativeBiometrics.FaceID:
          setBiometryType(EBiometricsType.FACE_ID);
          break;
        case ReactNativeBiometrics.TouchID:
          setBiometryType(EBiometricsType.TOUCH_ID);
          break;
        case ReactNativeBiometrics.Biometrics:
          setBiometryType(EBiometricsType.BIOMETRICS);
          break;
        default:
          setIsBiometricEnable(false);
          storeBiometricEnable(false);
          setBiometryType(EBiometricsType.NOT_SUPPORT);
          break;
      }
    };

    getBiometricType();
  }, [biometryType]);

  return { onBiometricsLoginPress, biometryType, isBiometricEnable };
};
